import { useMemo } from 'react'

import { useFragment } from '@apollo/client'
import {
  EditSurveySurveyFragmentDoc,
  EngagementPublishSurveyDocument,
  EngagementUpdateSurveySettingsDocument,
  FormsScheduleRecurrenceTypeEnum,
} from 'generated/resources/graphql'
import compact from 'lodash/compact'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import { SurveyWizard } from 'modules/engagement_surveys/components/SurveyWizard'
import { mapRecurrenceTypeFromBackend } from 'modules/engagement_surveys/lib/formRecurrenceMapper'
import {
  scheduleStartDateFromUTC,
  scheduleStartDateToUTC,
} from 'modules/engagement_surveys/lib/getScheduleStartDate'
import { validRespondentStrategy } from 'modules/engagement_surveys/lib/validRespondentStrategy'

interface Props {
  surveyId: number
  onClose: () => void
  onSuccess: () => void
  action: 'edit' | 'publish'
}

const isRecurrenceType = (
  recurrenceType: string
): recurrenceType is FormsScheduleRecurrenceTypeEnum => {
  return ['daily', 'monthly', 'onetime', 'quarterly', 'weekly', 'yearly'].includes(recurrenceType)
}

export function EditSurveyWizard({ surveyId, onClose, onSuccess, action = 'edit' }: Props) {
  const { data: survey } = useFragment({
    fragment: EditSurveySurveyFragmentDoc,
    fragmentName: 'EditSurveySurvey',
    from: {
      __typename: 'SurveysSurvey',
      id: surveyId,
    },
  })

  const surveyForm = survey?.form

  const [updateSurvey] = useMutation(EngagementUpdateSurveySettingsDocument, {
    extractorFn: (data) => data.surveysMutations.updateSurveysSurvey,
  })

  const [publishSurvey] = useMutation(EngagementPublishSurveyDocument, {
    extractorFn: (data) => data.surveysMutations.publishSurveysSurvey,
  })

  const respondentStrategy = useMemo(
    () =>
      survey?.respondentStrategy
        ? {
            strategy: survey.respondentStrategy.strategy ?? ('all_employees' as const),
            arguments: compact(survey.respondentStrategy.arguments),
          }
        : { strategy: 'all_employees' as const, arguments: [] },
    [survey?.respondentStrategy]
  )

  const editors =
    survey?.editors
      ?.map((editor) => editor?.employee)
      .filter((editor) => editor !== undefined)
      ?.map((editor) => ({
        id: editor.id ?? 0,
        fullName: editor.fullName ?? '',
        firstName: editor.firstName ?? '',
        lastName: editor.lastName ?? '',
        access: {
          id: editor.access?.id ?? 0,
          avatar: editor.access?.avatar ?? '',
        },
      })) ?? []

  // Use nextRunDate if available (for active surveys), otherwise use materialized form startsAt (for scheduled surveys)
  const nextRunDate = scheduleStartDateFromUTC(surveyForm?.nextRunDate)

  // For scheduled surveys that haven't started, get date/time from first materialized form's startsAt
  const materializedForm = surveyForm?.materializedForms?.[0]
  const materializedFormStartsAt = materializedForm?.startsAt
    ? scheduleStartDateFromUTC(materializedForm.startsAt)
    : null

  const startDateTime = nextRunDate || materializedFormStartsAt

  // Calculate publish validation errors
  const publishValidationErrors = useMemo(() => {
    const errors: string[] = []

    if (surveyForm?.title === i18n.t('surveys.default_title') || !surveyForm?.title) {
      errors.push(i18n.t('surveys.publish_dialog.errors.default_title'))
    }

    if (surveyForm?.elementsConnection?.totalCount === 0) {
      errors.push(i18n.t('surveys.publish_dialog.errors.no_elements'))
    }

    if (!respondentStrategy || !validRespondentStrategy(respondentStrategy)) {
      errors.push(i18n.t('surveys.publish_dialog.errors.no_participants'))
    }

    return errors
  }, [surveyForm?.title, surveyForm?.elementsConnection?.totalCount, respondentStrategy])

  return (
    <SurveyWizard
      title={
        action === 'publish'
          ? i18n.t('surveys.publish_dialog.title')
          : i18n.t('surveys.edit_survey_dialog.title')
      }
      surveyId={surveyId}
      action={action}
      publishValidationErrors={publishValidationErrors}
      initialValues={{
        title: surveyForm?.title || '',
        description: surveyForm?.description || '',
        anonymous: surveyForm?.anonymous || false,
        restrictedViewersEnabled: survey?.restrictedViewersEnabled || false,
        editors,
        respondentStrategy,
        startDate: startDateTime?.format('YYYY-MM-DD') || '',
        startTime: startDateTime?.format('HH:mm') || '',
        recurrenceType: mapRecurrenceTypeFromBackend(surveyForm?.recurrenceType),
        daysToAnswer: surveyForm?.configuration?.daysToAnswer ?? undefined,
        endsAt: surveyForm?.endsAt || undefined,
      }}
      onClose={onClose}
      onSubmit={async (data) => {
        const dataRespondentStrategy = data.respondentStrategy

        const respondentStrategyInput =
          dataRespondentStrategy.strategy &&
          dataRespondentStrategy.arguments?.filter((argument) => argument !== undefined)
            ? {
                strategy: dataRespondentStrategy.strategy,
                arguments:
                  dataRespondentStrategy.arguments.filter((argument) => argument !== undefined) ||
                  [],
              }
            : undefined

        if (!respondentStrategyInput) {
          throw new Error('Respondent strategy is required')
        }

        // Always update the survey first
        const updateResult = await updateSurvey({
          variables: {
            surveyId,
            title: data.title,
            anonymous: data.anonymous,
            description: data.description,
            respondentStrategy: respondentStrategyInput,
            restrictedViewersEnabled: data.restrictedViewersEnabled,
            editorIds:
              data.editors.map((editor) => editor.id).filter((id) => id !== undefined) || [],
          },
        })
        const updateHandled = handleResult(updateResult, () => null)

        if (!updateHandled.success || !updateHandled.data?.survey) {
          return
        }

        if (action === 'publish') {
          const date = scheduleStartDateToUTC({
            startDate: data.startDate,
            startTime: data.startTime,
          })

          const recurrenceTypeValue = isRecurrenceType(data.recurrenceType || ('onetime' as const))
            ? data.recurrenceType || ('onetime' as const)
            : ('onetime' as const)

          const publishResult = await publishSurvey({
            variables: {
              daysToAnswer: data.daysToAnswer,
              endsAt: data.endsAt,
              recurrenceType: recurrenceTypeValue,
              startDate: date?.format('YYYY-MM-DD'),
              startTime: date?.format('HH:mm'),
              surveyId,
            },
          })
          const publishHandled = handleResult(publishResult, () => null)

          if (publishHandled.success && publishHandled.data?.survey) {
            onSuccess()
          }
        } else {
          onSuccess()
        }
      }}
    />
  )
}
